import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
// Import images for bundling
import imageBiometric from '../images/Biometric.png';
import imageWhySizingMatters from '../images/WhySizingMatters.png';
import imageFullOfFlare from '../images/FullOfFlare.png';
import imageMyofunctionalPacifier from '../images/MyofunctionalPacifier.png';
import imageOrthodonticPacifier from '../images/OrthodonticPacifier.png';
import imageConventionalPacifier from '../images/ConventionalPacifier.png';

const slidesBioSizing = [
    {
        key: 's1',
        text: 'What is biometric sizing?',
        desc: 'Parents struggle with the decision to buy the perfect brand and size pacifier for their baby. There is no size standardization from one company to another. Recent science supports the concept of "proper pacifier fit". A too small or poorly designed pacifier can cause orthodontic problems that last a lifetime.',
        image: imageBiometric,
    },
    {
        key: 's2',
        text: 'Why sizing matters',
        desc: `The pacifier size plays a bigger role than the design of the pacifier itself. Sizing by chronological age is unorganized, non-standardized and unreliable. A pacifier must fit the size of your baby's mouth (palate). Chronological age is not a size metric.`,
        image: imageWhySizingMatters,
    },
    {
        key: 's3',
        text: 'Biometric shielding',
        desc: `Most pacifiers have a flat, straight shield that presses in and up against a baby's chin when they suck. But some companies, like Boon, are now making shields that allow your baby's jaw to move freely for growing and breathing.`,
        image: imageFullOfFlare,
    },
];

const slidesDesigns = [
    {
      key: 's1',
      text: 'Myofunctional pacifier',
      desc: `An ergonomic pacifier specifically developed to encourage proper interaction with the tongue. These designs may look a bit different but function in cooperation with the tongue and palate.`,
      image: imageMyofunctionalPacifier,
    },
    {
      key: 's2',
      text: 'Orthodontic pacifier',
      desc: `An orthodontic nipple shape is asymmetrical with a scooped bottom. Claims of benefit that these pacifiers encourage oral healthy development vary from brand to brand.`,
      image: imageOrthodonticPacifier,
    },
    {
      key: 's3',
      text: 'Conventional pacifier',
      desc: `These are the round symmetrical pacifiers initially intended to be used only in newborns. They are usually too small as babies get older, and they don't support the palate of the growing baby.`,
      image: imageConventionalPacifier,
    },
];

const LearnMorePage = () => {
    const [sliderType, setSliderType] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderRef = useRef(null);

    // Scroll slider card to top when slide changes
    useEffect(() => {
        if (sliderRef.current) {
            sliderRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [currentSlide]);

    const slides =
        sliderType === 'bio' ? slidesBioSizing : sliderType === 'design' ? slidesDesigns : [];

    const handleNext = () => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
    };

    const handlePrev = () => {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    };

    const resetSlider = () => {
        setSliderType(null);
        setCurrentSlide(0);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <Sidebar isOpen={false} toggle={() => {}} type={false} />
            <Navbar toggle={() => {}} margin={true} type={false} lightTheme={true} />
            <div style={styles.container}>
                <header style={styles.header}>
                    <h1 style={styles.headerTitle}>Learn More</h1>
                </header>
                {sliderType === null ? (
                    <div style={styles.buttonSection}>
                        <button
                            style={styles.buttonContainer}
                            onClick={() => setSliderType('bio')}
                        >
                            Biometric Sizing
                        </button>
                        <button
                            style={styles.buttonContainer}
                            onClick={() => setSliderType('design')}
                        >
                            Pacifier Design
                        </button>
                    </div>
                ) : (
                    <div style={styles.sliderSection} ref={sliderRef}>
                        <img
                            src={slides[currentSlide].image}
                            alt={slides[currentSlide].text}
                            style={styles.slideImage}
                        />
                        <h2 style={styles.slideTitle}>{slides[currentSlide].text}</h2>
                        <p style={styles.slideText}>{slides[currentSlide].desc}</p>
                        <div style={styles.sliderNav}>
                            <button style={styles.navButton} onClick={handlePrev}>
                                Previous
                            </button>
                            <button style={styles.navButton} onClick={handleNext}>
                                Next
                            </button>
                        </div>
                        <button style={styles.backButton} onClick={resetSlider}>
                            Back
                        </button>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
};

const styles = {
    container: {
        fontFamily: "'Roboto', sans-serif",
        backgroundColor: '#fafafa',
        minHeight: '100vh',
        padding: '40px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    header: {
        borderBottom: '1px solid #e0e0e0',
        marginBottom: '30px',
        paddingBottom: '10px',
        textAlign: 'center',
    },
    headerTitle: {
        fontSize: '32px',
        fontWeight: '500',
        color: '#333',
    },
    buttonSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '25px',
    },
    buttonContainer: {
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px 32px',
        fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        minWidth: '280px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        letterSpacing: '0.2px',
    },
    sliderSection: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
        padding: '30px',
        textAlign: 'center',
        maxWidth: '600px',
        width: '100%',
    },
    slideImage: {
        width: '100%',
        borderRadius: '8px',
        marginBottom: '20px',
    },
    slideTitle: {
        fontSize: '26px',
        fontWeight: '500',
        color: '#42b5c8',
        marginBottom: '10px',
    },
    slideText: {
        fontSize: '16px',
        color: '#555',
        marginBottom: '20px',
        lineHeight: '1.6',
    },
    sliderNav: {
        display: 'flex',
        justifyContent: 'center',
        gap: '20px',
        marginBottom: '20px',
    },
    navButton: {
        backgroundColor: '#fff',
        color: '#424242',
        border: '1px solid #ddd',
        borderRadius: '6px',
        padding: '10px 25px',
        cursor: 'pointer',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        transition: 'background-color 0.3s, box-shadow 0.3s',
    },
    backButton: {
        marginTop: '20px',
        backgroundColor: 'transparent',
        border: 'none',
        color: '#424242',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontSize: '16px',
    },
};

export default LearnMorePage;